/**
 * Agent Selector Component
 * Admin-only component to select one or more sub-agents for dashboard aggregation
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../lib/AuthContext";
import XanoAPI from "../../../lib/XanoAPI";
import { useDashboardFilters } from "../contexts/DashboardFilterContext";
import { AgentOption } from "../types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { X } from "lucide-react";
import { Button } from "../../ui/button";

export const AgentSelector: React.FC = () => {
  const api = XanoAPI.getInstance();
  const auth = useAuth();
  const { filters, updateFilters } = useDashboardFilters();

  const isAdmin = auth?.currentUser?.user_permissions?.[0]?.role === "admin";

  // Fetch agents for admin
  const { data: agentsData, isLoading } = useQuery({
    queryKey: ["agents", auth?.currentUser?.agency_id],
    queryFn: async () => {
      if (!isAdmin || !auth?.currentUser?.agency_id) return [];

      try {
        const response = await api.getUsersByAgencyId(
          auth.currentUser.agency_id
        );
        const members = response?.[0]?.members || [];
        return members.map((member: any) => ({
          id: member.id,
          name: member.name || `${member.firstName || ""} ${member.lastName || ""}`.trim() || "Unknown",
          email: member.email,
        })) as AgentOption[];
      } catch (error) {
        console.error("❌ Error fetching agents:", error);
        return [];
      }
    },
    enabled: isAdmin && !!auth?.currentUser?.agency_id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // If not admin, don't render
  if (!isAdmin) {
    return null;
  }

  const selectedAgents = (agentsData || []).filter((agent) =>
    filters.selectedAgentIds.includes(agent.id)
  );

  const handleAgentSelect = (agentId: string) => {
    const id = parseInt(agentId);
    if (isNaN(id)) return;

    if (filters.selectedAgentIds.includes(id)) {
      // Remove agent
      updateFilters({
        selectedAgentIds: filters.selectedAgentIds.filter((aid) => aid !== id),
      });
    } else {
      // Add agent
      updateFilters({
        selectedAgentIds: [...filters.selectedAgentIds, id],
      });
    }
  };

  const handleRemoveAgent = (agentId: number) => {
    updateFilters({
      selectedAgentIds: filters.selectedAgentIds.filter((aid) => aid !== agentId),
    });
  };

  const handleSelectAll = () => {
    if (agentsData) {
      updateFilters({
        selectedAgentIds: agentsData.map((a) => a.id),
      });
    }
  };

  const handleClearAll = () => {
    updateFilters({
      selectedAgentIds: [],
    });
  };

  return (
    <div className="space-y-2.5">
      {/* Header */}
      <div className="flex items-center justify-between pb-2 border-b">
        <Label htmlFor="agent-selector" className="text-xs font-semibold text-foreground">Select Agents</Label>
        <div className="flex gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            disabled={isLoading || !agentsData?.length}
            className="h-7 text-xs px-2"
          >
            All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAll}
            disabled={filters.selectedAgentIds.length === 0}
            className="h-7 text-xs px-2"
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Agent Selector */}
      <Select
        value={undefined}
        onValueChange={handleAgentSelect}
        disabled={isLoading}
      >
        <SelectTrigger 
          id="agent-selector" 
          className={`h-9 text-sm ${
            filters.selectedAgentIds.length > 0 ? 'border-accent bg-accent/5' : ''
          }`}
        >
          <SelectValue placeholder={isLoading ? "Loading agents..." : "Select agents to view"} />
        </SelectTrigger>
        <SelectContent>
          {agentsData?.map((agent) => (
            <SelectItem
              key={agent.id}
              value={String(agent.id)}
              disabled={filters.selectedAgentIds.includes(agent.id)}
            >
              {agent.name} {agent.email && `(${agent.email})`}
            </SelectItem>
          ))}
          {agentsData && agentsData.length === 0 && (
            <SelectItem value="no-agents" disabled>
              No agents found
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      {/* Selected agents badges */}
      {selectedAgents.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <p className="text-xs text-muted-foreground w-full mb-1.5 font-medium">
            Selected ({selectedAgents.length}):
          </p>
          {selectedAgents.map((agent) => (
            <Badge
              key={agent.id}
              variant="secondary"
              className="flex items-center gap-1.5 text-xs px-2 py-1 bg-accent/10 text-accent border-accent/20"
            >
              {agent.name}
              <button
                onClick={() => handleRemoveAgent(agent.id)}
                className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                aria-label={`Remove ${agent.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {filters.selectedAgentIds.length === 0 && (
        <p className="text-xs text-muted-foreground pt-2 border-t">
          No agents selected. Showing your own clients.
        </p>
      )}
    </div>
  );
};



